@if (clientService.loading()) {
  <div class="py-1 client-skeleton-wrapper">
    <bai-client-info-skeleton *ngFor="let i of [1, 2, 3, 4, 5, 6]" />
    <div class="fade-gradient-overlay"></div>
  </div>
} @else {
  <div class="flex flex-col h-full">

    <!-- Panel title — expanded only; updates with active/archived view -->
    @if (!collapsed) {
      <div class="bai-client-list-title">{{ showArchived() ? 'Archived' : 'Traffic Control' }}</div>
    }

    <!-- Scrollable area -->
    <div>
      <ng-container *ngTemplateOutlet="showArchived() ? archived : active"></ng-container>
    </div>

    <!-- Footer toggle -->
    <div class="flex items-center justify-center border-t-2 border-gray-200 pt-2">
      <button (click)="viewArchived()">
        @if (!showArchived()) {
          <i
            class="pi pi-inbox mr-2"
            aria-label="Toggle view"
            [pTooltip]="collapsed ? 'View Archived Conversations' : undefined"
            tooltipPosition="top"
          ></i>
          @if (!collapsed) {View Archived Conversations}
        } @else {
          <i
            class="pi pi-envelope mr-2"
            aria-label="Toggle view"
            [pTooltip]="collapsed ? 'View Active Conversations' : undefined"
            tooltipPosition="top"
          ></i>
          @if (!collapsed) {View Active Conversations}
        }
      </button>
    </div>
  </div>
}

<!-- ============================================================
     ACTIVE VIEW — Now / Next / Later queue
============================================================ -->
<ng-template #active>
  @if (!collapsed) {
    <!-- Single filter dropdown -->
    <div class="bai-queue-filter-wrap" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
      <button
        type="button"
        class="bai-queue-filter-trigger"
        [class.bai-queue-filter-trigger--active]="queueState.activeFilter() !== 'all'"
        (click)="toggleFilterDropdown($event)"
        [attr.aria-expanded]="showFilterDropdown()"
        aria-haspopup="listbox"
        aria-label="Filter clients"
      >
        <i class="pi pi-filter" aria-hidden="true"></i>
        <span>Filter: <strong>{{ activeFilterLabel() }}</strong></span>
        <i class="pi pi-chevron-down bai-queue-filter-trigger__caret" aria-hidden="true"></i>
      </button>

      @if (showFilterDropdown()) {
        <div class="bai-queue-filter-dropdown" role="listbox" aria-label="Filter options">
          @for (chip of filterChips; track chip.id) {
            <button
              type="button"
              class="bai-queue-filter-dropdown-item"
              [class.bai-queue-filter-dropdown-item--active]="queueState.activeFilter() === chip.id"
              role="option"
              [attr.aria-selected]="queueState.activeFilter() === chip.id"
              (click)="setFilter(chip.id); showFilterDropdown.set(false)"
            >
              <i
                class="pi pi-check bai-queue-filter-dropdown-item__check"
                [class.bai-queue-filter-dropdown-item__check--visible]="queueState.activeFilter() === chip.id"
                aria-hidden="true"
              ></i>
              {{ chip.label }}
            </button>
          }
        </div>
      }
    </div>
  }

  <!-- Queue sections scroll panel -->
  <p-scrollPanel
    #clientListScrollPanel
    styleClass="pb-2"
    [style]="{ height: collapsed ? 'calc(100vh - 133px - .5rem)' : 'calc(100vh - 200px - .5rem)' }"
  >
    @for (section of queueState.sections(); track section.bucket) {
      @if (!collapsed) {
        <!-- Section header -->
        <div class="bai-queue-section-header">
          <span class="bai-queue-section-header__label bai-queue-section-header__label--{{ section.bucket }}">
            {{ section.label }}
          </span>
          <span class="bai-queue-section-header__count">{{ section.rows.length }}</span>
        </div>
      }

      @if (section.rows.length === 0 && !collapsed) {
        <!-- Empty state per section -->
        <div class="bai-queue-empty">
          @switch (section.bucket) {
            @case ('now') {
              <span>No urgent clients right now.</span>
              <span class="bai-queue-empty__hint">Check Next for upcoming needs.</span>
            }
            @case ('next') {
              <span>Nothing queued for Next.</span>
              <span class="bai-queue-empty__hint">All clients are on track.</span>
            }
            @case ('later') {
              <span>No clients in Later.</span>
              <span class="bai-queue-empty__hint">Your queue is clear.</span>
            }
          }
        </div>
      }

      @for (row of section.rows; track row.session.uiSessionId) {
        <div
          [attr.data-conversation]="row.session.conversationId"
          tabindex="-1"
          class="bai-queue-row-wrap"
        >
          <bai-client-queue-row
            [row]="row"
            [selected]="row.session.conversationId === selectedConversation()"
            [collapsed]="collapsed"
            [qualityRisk]="qualityRiskFor(row)"
            (select)="onQueueRowSelect($event)"
            (rowAction)="onRowAction($event)"
          />
        </div>
      }
    }
  </p-scrollPanel>
</ng-template>

<!-- ============================================================
     ARCHIVED VIEW — unchanged from before
============================================================ -->
<ng-template
  #clientInfoList
  let-sessions
  let-showArchive="showArchive"
  let-archived="archived"
  let-scrollHeight="scrollHeight"
  let-showGuid="showGuid"
>
  <p-scrollPanel #clientListScrollPanel styleClass="pb-2" [style]="{ height: scrollHeight }">
    <ng-container *ngFor="let session of sessions">
      <div
        [attr.data-conversation]="session.conversationId"
        tabindex="-1"
        style="margin-bottom: .5rem"
      >
        <bai-client-info
          [session]="session"
          [selected]="session.conversationId === selectedConversation()"
          [highlight]="(highlightedConversations$ | async)?.has(session.subjectId + ':' + session.conversationId)"
          [showArchiveButton]="showArchive"
          [showGuid]="showGuid"
          [collapsed]="collapsed"
          [archived]="archived"
        />
      </div>
    </ng-container>
  </p-scrollPanel>
</ng-template>

<ng-template #archived>
  <ng-container
    *ngTemplateOutlet="clientInfoList; context: {
      $implicit: clientService.archivedSessions(),
      showArchive: false,
      archived: true,
      showGuid: true,
      scrollHeight: collapsed ? 'calc(100vh - 133px - .5rem)' : 'calc(100vh - 170px - .5rem)'
    }"
  ></ng-container>
</ng-template>
